/**
 * KnowledgeGraph - Container for ABox (assertional) data.
 *
 * Replaces the ABox portion of the deprecated DomainModel class.
 * Contains individuals and relationships.
 *
 * Implements the Graph interface from semantical-core.
 */

import type { Graph } from "@semantical/modules/semantical-core";
import type { ObjectProperty, ClassHierarchyMap } from "@semantical/modules/ontology-model";
import { SchemaClass, OntologySchema } from "@semantical/modules/ontology-model";
import Individual from "./Individual.ts";
import Relationship from "./Relationship.ts";
import Vocabulary from "@semantical/modules/vocabulary/Vocabulary.ts";

export default class KnowledgeGraph implements Graph<Individual, Relationship> {
    /** Unique identifier for this knowledge graph (matches DatasetRef.id) */
    id: string;
    name: string;
    uri: string;
    individuals: Map<string, Individual> = new Map<string, Individual>();
    relationships: Map<string, Relationship> = new Map<string, Relationship>();

    /**
     * Ontology imports for this knowledge graph.
     * Maps ontology IRI to optional version IRI.
     * Recorded when elements are added from a modeling language.
     */
    imports: Map<string, string | undefined> = new Map();

    /**
     * Namespace prefixes for this knowledge graph.
     * Maps namespace IRI to prefix string.
     * Recorded when elements are added from a modeling language.
     */
    prefixes: Map<string, string> = new Map();

    // Reference to ontology schema for type checking (optional, for future use)
    private _ontologySchema: OntologySchema | null = null;
    classHierarchy: ClassHierarchyMap | null = null;

    constructor(name: string, uri?: string, ontologySchema?: OntologySchema, id?: string) {
        this.id = id || crypto.randomUUID();
        this.name = name;
        this.uri = uri || `urn:kg:${name.toLowerCase().replace(/\s+/g, '-')}`;
        this._ontologySchema = ontologySchema || null;
    }

    /**
     * Generate a URI for a new individual using this graph's URI as base.
     * @param localName - The local name for the individual (will be URI-encoded)
     */
    generateIndividualUri(localName: string): string {
        const encoded = encodeURIComponent(localName.replace(/\s+/g, '_'));
        // Use # separator if URI doesn't end with / or #
        const separator = this.uri.endsWith('/') || this.uri.endsWith('#') ? '' : '#';
        return `${this.uri}${separator}${encoded}`;
    }

    /**
     * All nodes (individuals) in this graph.
     * Implements Graph interface.
     */
    get nodes(): Iterable<Individual> {
        return this.individuals.values();
    }

    /**
     * All edges (relationships) in this graph.
     * Implements Graph interface.
     */
    get edges(): Iterable<Relationship> {
        return this.relationships.values();
    }

    /**
     * Set the ontology schema for type validation
     */
    setOntologySchema(schema: OntologySchema | null): void {
        this._ontologySchema = schema;
    }

    /**
     * Get the ontology schema (for type validation)
     */
    getOntologySchema(): OntologySchema | null {
        return this._ontologySchema;
    }

    /**
     * Set the class hierarchy for subclass checking
     */
    setClassHierarchy(hierarchy: ClassHierarchyMap) {
        this.classHierarchy = hierarchy;
    }

    /**
     * Add an ontology import to this knowledge graph.
     * Called when adding elements from a modeling language.
     *
     * @param ontologyIri - The ontology IRI
     * @param versionIri - Optional version IRI for the ontology
     */
    addImport(ontologyIri: string, versionIri?: string): void {
        // Only add if not already imported (first import wins for version)
        if (!this.imports.has(ontologyIri)) {
            this.imports.set(ontologyIri, versionIri);
        }
    }

    /**
     * Get all imported ontology IRIs.
     */
    getImports(): Map<string, string | undefined> {
        return new Map(this.imports);
    }

    /**
     * Add a namespace prefix to this knowledge graph.
     * Called when adding elements from a modeling language.
     *
     * @param namespace - The namespace IRI (e.g., "http://semantical.io/ontology/core#")
     * @param prefix - The prefix string (e.g., "semcore")
     */
    addPrefix(namespace: string, prefix: string): void {
        // Only add if not already defined (first definition wins)
        if (!this.prefixes.has(namespace)) {
            this.prefixes.set(namespace, prefix);
        }
    }

    /**
     * Get all namespace prefixes.
     */
    getPrefixes(): Map<string, string> {
        return new Map(this.prefixes);
    }

    /**
     * Check if a class is equal to or a subclass of a target class
     */
    isSubclassOf(classUri: string, targetUri: string): boolean {
        if (classUri === targetUri) return true;
        if (!this.classHierarchy) return false;

        // Check ancestors recursively with cycle detection
        const visited = new Set<string>();
        const checkAncestors = (uri: string): boolean => {
            if (visited.has(uri)) return false;
            visited.add(uri);

            const parents = this.classHierarchy!.get(uri);
            if (!parents) return false;

            for (const parentUri of parents) {
                if (parentUri === targetUri) return true;
                if (checkAncestors(parentUri)) return true;
            }
            return false;
        };

        return checkAncestors(classUri);
    }

    /**
     * Find an Individual by its identifying URI.
     */
    getNodeByUri(uri: string): Individual | undefined {
        return Array.from(this.individuals.values()).find(
            i => i.uri === uri
        );
    }

    /**
     * Find an Individual by its URI.
     * Alias for getNodeByUri for consistency with other Individual lookup methods.
     */
    getIndividualByUri(uri: string): Individual | undefined {
        return this.getNodeByUri(uri);
    }

    /**
     * Add an individual to the knowledge graph.
     *
     * @param individual - The individual to add
     * @param ontologyInfo - Optional ontology metadata (IRI, version, namespace, prefix) to record
     */
    addIndividual(
        individual: Individual,
        ontologyInfo?: {
            ontologyIri: string;
            versionIri?: string;
            namespace?: string;
            prefix?: string;
        }
    ): void {
        // Check if an individual with this concept URI already exists
        const existingIndividual = Array.from(this.individuals.values()).find(
            i => i.concept.uri === individual.concept.uri
        );

        if (existingIndividual) {
            console.warn(`Individual with concept URI "${individual.concept.uri}" already exists in the knowledge graph. Skipping.`);
            return;
        }

        this.individuals.set(individual.name, individual);

        // Record ontology import if provided
        if (ontologyInfo) {
            this.addImport(ontologyInfo.ontologyIri, ontologyInfo.versionIri);

            // Record namespace prefix if provided
            if (ontologyInfo.namespace && ontologyInfo.prefix) {
                this.addPrefix(ontologyInfo.namespace, ontologyInfo.prefix);
            }
        }
    }

    /**
     * Remove an individual from the knowledge graph
     */
    removeIndividual(individual: Individual): void {
        this.individuals.delete(individual.name);
        // Also remove any relationships that reference this individual
        const toRemove: string[] = [];
        this.relationships.forEach((relationship, id) => {
            if (relationship.subject === individual || relationship.object === individual) {
                toRemove.push(id);
            }
        });
        toRemove.forEach(id => this.relationships.delete(id));
    }

    /**
     * Add a relationship to the knowledge graph
     */
    addRelationship(relationship: Relationship): void {
        this.relationships.set(relationship.id, relationship);
    }

    /**
     * Remove a relationship from the knowledge graph
     */
    removeRelationship(relationship: Relationship): void {
        this.relationships.delete(relationship.id);
    }

    /**
     * Get a relationship by its ID
     */
    getRelationshipById(id: string): Relationship | undefined {
        return this.relationships.get(id);
    }

    /**
     * Get an edge (relationship) by its ID.
     * Implements Graph interface.
     */
    getEdgeById(id: string): Relationship | undefined {
        return this.relationships.get(id);
    }

    /**
     * Get all individuals that are instances of a specific class or any of its subclasses
     */
    getIndividualsByClassUri(classUri: string): Individual[] {
        return Array.from(this.individuals.values()).filter(
            individual => this.isSubclassOf(individual.modelClass.uri, classUri)
        );
    }

    /**
     * Serialize the knowledge graph to Turtle format.
     *
     * @param workspacePrefixes - Optional prefix map from Workspace.getPrefixes() (namespace → prefix)
     */
    toTurtle(workspacePrefixes?: Map<string, string>): Promise<string> {
        // Start with standard W3C prefixes (always available)
        const knownPrefixes: Record<string, string> = {
            'http://www.w3.org/1999/02/22-rdf-syntax-ns#': 'rdf',
            'http://www.w3.org/2000/01/rdf-schema#': 'rdfs',
            'http://www.w3.org/2002/07/owl#': 'owl',
            'http://www.w3.org/2001/XMLSchema#': 'xsd',
        };

        // Add prefixes from KG's explicit prefix registry (from ModelingLanguages)
        for (const [namespace, prefix] of this.prefixes) {
            knownPrefixes[namespace] = prefix;
        }

        // Add prefixes from workspace (all loaded ontologies) as fallback
        if (workspacePrefixes) {
            for (const [namespace, prefix] of workspacePrefixes) {
                // Don't override KG's explicit prefixes
                if (!knownPrefixes[namespace]) {
                    knownPrefixes[namespace] = prefix;
                }
            }
        }

        // Add the KG's own URI as the default namespace prefix
        // Ensure it ends with # or / for proper prefix usage
        const kgNamespace = this.uri.endsWith('#') || this.uri.endsWith('/')
            ? this.uri
            : this.uri + '#';
        knownPrefixes[kgNamespace] = '';

        // Helper to extract namespace from a URI
        const extractNamespace = (uri: string): string | null => {
            const hashIndex = uri.lastIndexOf('#');
            if (hashIndex > 0) {
                return uri.substring(0, hashIndex + 1);
            }
            const slashIndex = uri.lastIndexOf('/');
            if (slashIndex > 0) {
                return uri.substring(0, slashIndex + 1);
            }
            return null;
        };

        // Helper to generate a prefix name from a namespace
        const generatePrefixFromNamespace = (namespace: string): string => {
            // Try to extract a meaningful name from the namespace
            // e.g., http://semantical.io/ontology/core# -> "core"
            // e.g., http://archimate.io/metamodel# -> "metamodel"
            const withoutTrailing = namespace.replace(/[#/]$/, '');
            const lastSegment = withoutTrailing.split('/').pop() || 'ns';
            // Sanitize to valid prefix (lowercase, alphanumeric)
            return lastSegment.toLowerCase().replace(/[^a-z0-9]/g, '');
        };

        // Collect all URIs used in individuals (class URIs, property URIs, relationship URIs)
        const allUris = new Set<string>();
        this.individuals.forEach((individual) => {
            allUris.add(individual.modelClass.uri);
            individual.propertyValues.forEach((_, propertyUri) => {
                allUris.add(propertyUri);
            });
        });
        this.relationships.forEach((rel) => {
            allUris.add(rel.predicate.uri);
        });

        // Deduce prefixes for namespaces not already known
        const usedPrefixNames = new Set(Object.values(knownPrefixes));
        for (const uri of allUris) {
            const namespace = extractNamespace(uri);
            if (namespace && !knownPrefixes[namespace]) {
                // Generate a unique prefix name
                let baseName = generatePrefixFromNamespace(namespace);
                let prefixName = baseName;
                let counter = 1;
                while (usedPrefixNames.has(prefixName)) {
                    prefixName = `${baseName}${counter}`;
                    counter++;
                }
                knownPrefixes[namespace] = prefixName;
                usedPrefixNames.add(prefixName);
            }
        }

        // Collect which prefixes are actually used
        const usedPrefixes = new Set<string>(['rdf', 'rdfs', 'owl']);

        // Helper to shorten a URI using known prefixes
        const formatUri = (uri: string): string => {
            for (const [ns, prefix] of Object.entries(knownPrefixes)) {
                if (uri.startsWith(ns)) {
                    usedPrefixes.add(prefix);
                    return `${prefix}:${uri.substring(ns.length)}`;
                }
            }
            return `<${uri}>`;
        };

        // Helper to escape string literals
        const escapeString = (s: string) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

        // Content lines (will prepend prefixes later)
        const contentLines: string[] = [];

        // Header comment
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push(`# Knowledge Graph: ${this.name}`);
        contentLines.push(`# Exported: ${new Date().toISOString()}`);
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('');

        // Ontology declaration with imports
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('# Ontology Declaration');
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('');

        // Build ontology declaration
        const kgOntologyUri = this.uri.endsWith('#') || this.uri.endsWith('/')
            ? this.uri.replace(/[#/]$/, '')
            : this.uri;

        if (this.imports.size === 0) {
            contentLines.push(`<${kgOntologyUri}> a owl:Ontology .`);
        } else {
            contentLines.push(`<${kgOntologyUri}> a owl:Ontology ;`);

            const importEntries = Array.from(this.imports.entries());
            importEntries.forEach(([ontologyIri, versionIri], index) => {
                // Use versionIRI if available, otherwise ontologyIRI
                const importIri = versionIri || ontologyIri;
                const isLast = index === importEntries.length - 1;
                contentLines.push(`    owl:imports <${importIri}>${isLast ? ' .' : ' ;'}`);
            });
        }
        contentLines.push('');

        // Individuals section
        if (this.individuals.size > 0) {
            contentLines.push('# -----------------------------------------------------------');
            contentLines.push('# Individuals');
            contentLines.push('# -----------------------------------------------------------');
            contentLines.push('');

            // Build a map of relationships by subject URI for grouping
            const relationshipsBySubject = new Map<string, Relationship[]>();
            this.relationships.forEach((relationship) => {
                const subjectUri = relationship.subject.uri;
                if (!relationshipsBySubject.has(subjectUri)) {
                    relationshipsBySubject.set(subjectUri, []);
                }
                relationshipsBySubject.get(subjectUri)!.push(relationship);
            });

            this.individuals.forEach((individual) => {
                const props: string[] = [];

                // Types (owl:NamedIndividual and the class)
                props.push(`    a owl:NamedIndividual ,`);
                props.push(`      ${formatUri(individual.modelClass.uri)} ;`);

                // Label
                props.push(`    rdfs:label "${escapeString(individual.name)}"`);

                // Definition
                if (individual.definition) {
                    props[props.length - 1] += ' ;';
                    props.push(`    rdfs:comment "${escapeString(individual.definition)}"`);
                }

                // Data property values (from propertyValues map - supports multi-value)
                individual.propertyValues.forEach((values, propertyUri) => {
                    // Find the property definition to get the range/datatype
                    const dataProp = individual.modelClass.dataProperties?.find(p => p.uri === propertyUri);
                    const range = dataProp?.range?.toLowerCase() || 'string';

                    // Format each value based on datatype
                    for (const value of values) {
                        props[props.length - 1] += ' ;';

                        let formattedValue: string;
                        if (range === 'date' || range === 'datetime') {
                            formattedValue = `"${escapeString(value)}"^^xsd:date`;
                            usedPrefixes.add('xsd');
                            if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                            }
                        } else if (range === 'integer' || range === 'int' || range === 'long' || range === 'short') {
                            formattedValue = `"${escapeString(value)}"^^xsd:integer`;
                            usedPrefixes.add('xsd');
                            if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                            }
                        } else if (range === 'decimal' || range === 'float' || range === 'double') {
                            formattedValue = `"${escapeString(value)}"^^xsd:decimal`;
                            usedPrefixes.add('xsd');
                            if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                            }
                        } else if (range === 'boolean') {
                            formattedValue = `"${value === 'true' ? 'true' : 'false'}"^^xsd:boolean`;
                            usedPrefixes.add('xsd');
                            if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                            }
                        } else {
                            // Default to string
                            formattedValue = `"${escapeString(value)}"`;
                        }

                        props.push(`    ${formatUri(propertyUri)} ${formattedValue}`);
                    }
                });

                // Relationships where this individual is the subject
                const rels = relationshipsBySubject.get(individual.uri) || [];
                rels.forEach((rel) => {
                    props[props.length - 1] += ' ;';
                    props.push(`    ${formatUri(rel.predicate.uri)} <${rel.object.uri}>`);
                });

                contentLines.push(formatUri(individual.uri));
                props[props.length - 1] += ' .';
                contentLines.push(...props);
                contentLines.push('');
            });
        }

        // Build prefix declarations (only for used prefixes)
        const prefixLines: string[] = [];
        const nsToPrefix = Object.entries(knownPrefixes);
        // Sort by prefix name for consistent output
        nsToPrefix.sort((a, b) => a[1].localeCompare(b[1]));

        for (const [ns, prefix] of nsToPrefix) {
            if (usedPrefixes.has(prefix)) {
                // Pad prefix for alignment
                const paddedPrefix = (prefix + ':').padEnd(6);
                prefixLines.push(`@prefix ${paddedPrefix}<${ns}> .`);
            }
        }
        prefixLines.push('');

        return Promise.resolve([...prefixLines, ...contentLines].join('\n'));
    }

    /**
     * Serialize to JSON-LD with per-object @context.
     */
    toJSON() {
        return {
            '@context': {
                '@vocab': 'http://semantical.io/ontology/core#',
                individuals: { '@id': 'hasIndividual', '@type': '@id', '@container': '@set' },
                relationships: { '@id': 'hasRelationship', '@type': '@id', '@container': '@set' },
                imports: { '@id': 'owl:imports', '@type': '@id', '@container': '@set' },
                prefixes: { '@id': 'hasPrefix', '@container': '@set' }
            },
            '@type': 'KnowledgeGraph',
            '@id': this.uri,
            id: this.id,
            name: this.name,
            uri: this.uri,
            individuals: Array.from(this.individuals.values()).map((individual) => individual.toJSON()),
            relationships: Array.from(this.relationships.values()).map((rel) => rel.toJSON()),
            imports: Array.from(this.imports.entries()).map(([ontologyIri, versionIri]) => ({
                ontologyIri,
                versionIri
            })),
            prefixes: Array.from(this.prefixes.entries()).map(([namespace, prefix]) => ({
                namespace,
                prefix
            }))
        };
    }

    static fromJSON(
        json: any,
        vocabulary: Vocabulary,
        modelClassResolver?: (uri: string) => SchemaClass | undefined,
        objectPropertyResolver?: (uri: string) => ObjectProperty | undefined,
        tboxIndividualResolver?: (uri: string) => import("@semantical/modules/semantical-core").Individual | undefined
    ): KnowledgeGraph {
        const knowledgeGraph = new KnowledgeGraph(json.name, json.uri, undefined, json.id);

        // Restore individuals
        (json.individuals ?? []).forEach((individual: any) => {
            knowledgeGraph.addIndividual(Individual.fromJSON(individual, vocabulary, modelClassResolver));
        });

        // Restore relationships (after individuals are loaded)
        // Support both old 'assertions' key and new 'relationships' key for backward compatibility
        const relationshipsJson = json.relationships ?? json.assertions ?? [];
        relationshipsJson.forEach((relJson: any) => {
            const relationship = Relationship.fromJSON(
                relJson,
                // Node resolver: first check ABox individuals, then fallback to TBox individuals
                (uri) => knowledgeGraph.getNodeByUri(uri) || tboxIndividualResolver?.(uri),
                objectPropertyResolver || (() => undefined)
            );
            if (relationship) {
                knowledgeGraph.relationships.set(relationship.id, relationship);
            }
        });

        // Restore ontology imports
        if (json.imports) {
            for (const imp of json.imports) {
                knowledgeGraph.addImport(imp.ontologyIri, imp.versionIri);
            }
        }

        // Restore namespace prefixes
        if (json.prefixes) {
            for (const pref of json.prefixes) {
                knowledgeGraph.addPrefix(pref.namespace, pref.prefix);
            }
        }

        return knowledgeGraph;
    }
}

export { KnowledgeGraph };
